<script setup lang="ts">
import type {
  AnalysisObject,
  MaskObject,
  PolylineObject,
  SegmentationData,
} from '@src/store/analysis/data-typing';

import type { DicomPullRequest } from '@/src/components/backend/payload-typing';

import { useUpdatedReportStore } from '@src/store/report-store';

import { computed, ref, useTemplateRef } from 'vue';

import {
  // getAllAsImages,
  getAllImageUIDs,
} from '@/src/components/backend/payload-tools';
import LoadSaveAnalysisStateDialog from '@/src/components/LoadSaveAnalysisStateDialog.vue';
import { useViewportActions } from '@/src/components/pixel-overlay/useViewportActions';
import { useAlert } from '@/src/composables/useAlert';
import { useMaskExtractor } from '@/src/composables/useMaskExtractor';
import { useAnalysisStore } from '@/src/store/analysis';
import { useContourContainerStore } from '@/src/store/contours/contour-container-store';
import {
  ContourLabel,
  encodeContourLabelItemId,
} from '@/src/store/contours/contour-definitions';
import { convertPointArrayToArray } from '@/src/store/contours/contour-processing/convertArrayToPointArray';
// import { useSegmentationSettingsStore } from '@/src/store/contours/contour-segmentation';
import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';
import { Orientation } from '@/src/store/dicom-web/classification-typing';
import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';
import { seriesClassification } from '@/src/store/dicom-web/SeriesClassification';

import { useAppState } from '@/src/store/ocg-app-state';

defineProps({
  showDialog: {
    type: Boolean,
    default: false,
  },
});

const { VITE_OCG_BACKEND_URL } = import.meta.env;

const analysisStore = useAnalysisStore();
const contourContainerStore = useContourContainerStore();
const viewportStateStore = useViewportStateStore();
// const segmentationSettingsStore = useSegmentationSettingsStore();
const currentStudyStore = useCurrentStudyStore();
const appState = useAppState();
const { getImageSegmentationGeometry } = useMaskExtractor();

const viewerIDs = ['ContoursLeft', 'ContoursRight'];

const alert = useAlert();
const isAnalysisActive = ref(false);
const showPreflightDialog = ref(false);
const calculateBMORE = ref(false);
const calculateBMOREMax = ref(false);
const calculateVasoconstrictionMap = ref(false);

const loadSaveAnalysisStateDialog = useTemplateRef<
  InstanceType<typeof LoadSaveAnalysisStateDialog>
>('loadSaveAnalysisStateDialog');

// ─── Confirmed Viewport Selections ─────────────────────────────────
interface ConfirmedViewportSelection {
  viewerID: string;
  seriesIndex: number;
  sliceIndex: number;
  /** Index into the sliceAnalysisInfo / dialog card (0 = left, 1 = right) */
  // Can expand in future if we need to support more than 2 viewers
  dialogSlotIndex: number;
}

const confirmedSelections = ref<ConfirmedViewportSelection[]>([]);

const hasDuplicateSelections = computed(() => {
  if (confirmedSelections.value.length < 2) return false;
  const [a, b] = confirmedSelections.value;
  return a.seriesIndex === b.seriesIndex && a.sliceIndex === b.sliceIndex;
});

/**
 * Deduplicated list — if both viewers point to the same series+slice
 * we only need to process once.
 */
const uniqueConfirmedSelections = computed(() => {
  if (!hasDuplicateSelections.value) return confirmedSelections.value;
  return [confirmedSelections.value[0]];
});

// ─── Slice Analysis Info ────────────────────────────────────────────
interface SliceAnalysisInfo {
  sliceIndex: number;
  viewerID: string;
  seriesIndex: number;
  imageCount: number;
  processedImageCount: number;
  breathHoldStart: number;
  breathHoldEnd: number;
  bmoreStart?: number;
  bmoreEnd?: number;
  segmentedPhaseCount: number;
  missingPhaseIndices: number[];
  excludedImageIndices: number[];
  loadingStatus: 'Pending' | 'Loading' | 'Complete';
}

const sliceAnalysisInfo = ref<SliceAnalysisInfo[]>([]);
const selectedSliceOrientation = ref<Record<string, Orientation>>({});
const isPreflightLoading = ref(false);
const preflightProcessedImages = ref(0);
const preflightTotalImages = ref(0);
const preflightCurrentSlice = ref<number | null>(null);
const preflightLoadError = ref<string | null>(null);
const orientationOptions = [
  Orientation.Undefined,
  Orientation.Sax,
  Orientation.Lax2CV,
  Orientation.Lax3CV,
  Orientation.Lax4CV,
].map(value => ({
  label: value,
  value,
}));

const hasSliceSummaries = computed(() => sliceAnalysisInfo.value.length > 0);
const hasMissingSegmentationPhases = computed(() =>
  sliceAnalysisInfo.value.some(
    sliceInfo => sliceInfo.missingPhaseIndices.length > 0,
  ),
);
const hasUnclassifiedOrientations = computed(() =>
  sliceAnalysisInfo.value.some((sliceInfo) => {
    const orientation
      = selectedSliceOrientation.value[String(sliceInfo.sliceIndex)]
        ?? seriesClassification.getClassificationByViewport(sliceInfo.viewerID)
          ?.orientation
          ?? Orientation.Undefined;

    return orientation === Orientation.Undefined;
  }),
);
const preflightProgress = computed(() => {
  if (preflightTotalImages.value <= 0) return 0;
  return (preflightProcessedImages.value / preflightTotalImages.value) * 100;
});

function toOrientation(value: Orientation | string) {
  return Object.values(Orientation).includes(value as Orientation)
    ? (value as Orientation)
    : Orientation.Undefined;
}

function recalculatePreflightProcessedImages() {
  preflightProcessedImages.value = sliceAnalysisInfo.value.reduce(
    (sum, item) => sum + item.processedImageCount,
    0,
  );
}

async function processSliceAnalysis(
  sliceInfo: SliceAnalysisInfo,
  orientation: Orientation,
) {
  const targetSlice = sliceInfo;

  const studyObj = useViewportActions().getStudyObjectByViewerId(
    targetSlice.viewerID,
  );
  if (!studyObj) {
    throw new Error(
      `Failed to retrieve study object for viewer ${targetSlice.viewerID}`,
    );
  }
  const breathholdState = studyObj.getBreathhold();
  const excludedImages = studyObj.getExcludedImages();
  if (!breathholdState || !excludedImages) {
    throw new Error(
      `Failed to retrieve breathhold state or excluded images for viewer ${targetSlice.viewerID}`,
    );
  }

  const imageUIDsfromStore
    = currentStudyStore.getSliceImageUIDsbyView(targetSlice.viewerID) ?? [];

  const missingPhaseIndices: number[] = [];
  let segmentedPhaseCount = 0;
  let processedImageCount = 0;

  for (let i = breathholdState.getStart(); i <= breathholdState.getEnd(); i++) {
    const imageUID = imageUIDsfromStore[i];

    if (imageUID && !excludedImages.has(imageUID)) {
      let hasSegmentation = false;

      try {
        await currentStudyStore.loadVtkImageAndMetaData(imageUID);
      }
      catch (error) {
        console.warn('Failed to load preflight image:', imageUID, error);
      }

      const { myoMask, endoContour, epiContour } = getImageSegmentationGeometry(
        imageUID,
        orientation,
        targetSlice.viewerID,
      );

      if (myoMask.length > 0) {
        hasSegmentation = true;
      }
      else {
        hasSegmentation = endoContour.length > 0 && epiContour.length > 0;
      }

      if (hasSegmentation) {
        segmentedPhaseCount += 1;
      }
      else {
        missingPhaseIndices.push(i + 1);
      }

      processedImageCount += 1;
      targetSlice.processedImageCount = processedImageCount;
      recalculatePreflightProcessedImages();

      if (processedImageCount % 2 === 0) {
        await new Promise<void>((resolve) => {
          setTimeout(resolve, 0);
        });
      }
    }
  }

  targetSlice.segmentedPhaseCount = segmentedPhaseCount;
  targetSlice.missingPhaseIndices = missingPhaseIndices;
}

async function rerunSliceAnalysis(
  sliceInfo: SliceAnalysisInfo,
  orientation: Orientation,
) {
  const targetSlice = sliceInfo;

  preflightLoadError.value = null;
  isPreflightLoading.value = true;
  preflightCurrentSlice.value = targetSlice.sliceIndex;
  targetSlice.loadingStatus = 'Loading';
  targetSlice.processedImageCount = 0;
  targetSlice.segmentedPhaseCount = 0;
  targetSlice.missingPhaseIndices = [];
  recalculatePreflightProcessedImages();
  await new Promise<void>((resolve) => {
    setTimeout(resolve, 0);
  });

  try {
    await processSliceAnalysis(sliceInfo, orientation);
    targetSlice.loadingStatus = 'Complete';
  }
  catch (error) {
    preflightLoadError.value
      = 'Failed while preparing slice data. Please close and reopen this dialog.';
    console.error('Preflight slice refresh failed:', error);
  }
  finally {
    preflightCurrentSlice.value = null;
    isPreflightLoading.value = false;
  }
}

async function handleSliceOrientationChange(
  sliceInfo: SliceAnalysisInfo,
  orientationValue: Orientation | string,
) {
  const orientation = toOrientation(orientationValue);
  const sliceKey = String(sliceInfo.sliceIndex);

  selectedSliceOrientation.value[sliceKey] = orientation;
  seriesClassification.adjustClassificationByViewport(sliceInfo.viewerID, {
    orientation,
    mode: 'Manual',
  });

  await rerunSliceAnalysis(sliceInfo, orientation);
}

function yieldToUI() {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, 0);
  });
}

// AbortController for cancelling in-flight analysis requests
let analysisAbortController: AbortController | null = null;

const imageUploadProgress = ref(0);
const imageUploadTotal = ref(0);
const uploadChunkSize = 80;

analysisStore.resetStudy();

// ─── getSegmentationData ────────────────────────────────────────────
async function getSegmentationData(dicomPullRequestObject: DicomPullRequest[]) {
  const segmentationData: SegmentationData = {};

  // Fast lookup: only process UIDs that are in the pull request
  const requestedUIDs = new Set(
    dicomPullRequestObject.map(r => r.SOPInstanceUID),
  );

  // Process each unique confirmed selection — no reverse-mapping needed

  for (const sel of uniqueConfirmedSelections.value) {
    const orientation
      = selectedSliceOrientation.value[String(sel.dialogSlotIndex)]
        ?? seriesClassification.getClassificationByViewport(sel.viewerID);

    const imageUIDsForSlice
      = currentStudyStore.getSliceImageUIDsbyView(sel.viewerID) ?? [];

    for (const imageUID of imageUIDsForSlice) {
      // Only process UIDs that are part of the pull request

      if (!requestedUIDs.has(imageUID)) continue;

      if (!contourContainerStore.contourContainers[imageUID]) continue;

      // Ensure the image is loaded

      await currentStudyStore.loadVtkImageAndMetaData(imageUID);

      const { myoMask, endoContour, epiContour } = getImageSegmentationGeometry(
        imageUID,
        orientation,
        sel.viewerID,
      );

      // Insertion point label depends on orientation. For SAX use RvInsertion, for LAX use LaxLvEndoBasalPoint
      const laxOrientations = new Set<Orientation>([
        Orientation.Lax2CV,
        Orientation.Lax3CV,
        Orientation.Lax4CV,
        Orientation.Lax5CV,
      ]);
      const insertionPointLabel = laxOrientations.has(orientation)
        ? ContourLabel.LaxLvEndoBasalPoint
        : ContourLabel.RvInsertion;

      // TODO: insertion point no longer an accurate name, use "reference point" or similar in future
      const [insertionPoint] = convertPointArrayToArray(
        contourContainerStore.contourContainers[imageUID].getContourPointArray({
          label: insertionPointLabel,
          id: encodeContourLabelItemId([0]),
        }),
      );

      if (!segmentationData[imageUID]) {
        segmentationData[imageUID] = [];
      }

      if (myoMask.length === 0) {
        if (endoContour.length === 0 || epiContour.length === 0) {
          alert.error(
            `No segmentation found for imageUID ${imageUID}. `
            + 'Please ensure all images are properly segmented and loaded.',
          );
          return null;
        }

        segmentationData[imageUID].push({
          type: 'PolylineObject',
          contourType: 'ENDO',
          data: endoContour,
        } as PolylineObject);

        segmentationData[imageUID].push({
          type: 'PolylineObject',
          contourType: 'EPI',
          data: epiContour,
        } as PolylineObject);
      }
      else {
        segmentationData[imageUID].push({
          type: 'MaskObject',
          maskType: 'MyoMask',
          data: myoMask as [number, number][],
        } as MaskObject);
      }

      segmentationData[imageUID].push({
        type: 'PolylineObject',
        contourType: 'insertionPoint',
        data: insertionPoint,
      } as PolylineObject);
    }
  }

  return segmentationData;
}

// ─── handleResponse ─────────────────────────────────────────────────
function handleResponse(responseData: any) {
  const biomarkerContainer = analysisStore.resetStudy();
  const analysisViewers = ['AnalysisLeft', 'AnalysisRight'];

  biomarkerContainer.populateFromResponse(responseData);

  const dynamicMaskTypes
    = biomarkerContainer.getMaskTypesBasedOnBiomarkerType('Dynamic');
  const staticMaskTypes
    = biomarkerContainer.getMaskTypesBasedOnBiomarkerType('Static');
  const polarMapTypes
    = biomarkerContainer.getMaskTypesBasedOnBiomarkerType('PolarMap');

  // Map each analysis viewer back to the confirmed selection.
  // When duplicated, both analysis viewers get the same indices
  // (from the single processed selection).
  for (let i = 0; i < analysisViewers.length; i++) {
    const viewID = analysisViewers[i];
    const sel = confirmedSelections.value[i];

    const sourceViewerID
      = sel?.viewerID ?? confirmedSelections.value[0].viewerID;
    const sourceIndices
      = viewportStateStore.getSelectedImageIndices(sourceViewerID);

    viewportStateStore.setSelectedImageIndices(viewID, sourceIndices, true);

    if (staticMaskTypes[0]) {
      viewportStateStore.setSelectedMarker(
        viewID,
        'Static',
        staticMaskTypes[0],
      );
    }
    if (dynamicMaskTypes[0]) {
      viewportStateStore.setSelectedMarker(
        viewID,
        'Dynamic',
        dynamicMaskTypes[0],
      );
    }

    if (polarMapTypes[0]) {
      viewportStateStore.setSelectedMarker(
        viewID,
        'PolarMap',
        polarMapTypes[0],
      );
    }
    viewportStateStore.setShowContours(viewID, false);
  }

  const reportStore = useUpdatedReportStore();
  reportStore.resetStore();
}

// ─── getImageUIDsBmoreUIDs ──────────────────────────────────────────
function getImageUIDsBmoreUIDs(includeBMORETarget = true) {
  const prefetchObject = {
    viewerIDs: [] as string[],
    include: [] as string[],
    allImageUIDs: [] as string[],
    excludedImageUIDs: [] as string[],
    bmoreTarget: {} as Record<string, [number, number]>,
    breathHoldCrops: {} as Record<string, { start: number; end: number }>,
  };

  // Use uniqueConfirmedSelections so we don't double-fetch for duplicates
  uniqueConfirmedSelections.value.forEach((sel) => {
    prefetchObject.viewerIDs.push(sel.viewerID);

    const imageUIDsfromStore
      = currentStudyStore.getSliceImageUIDsbyView(sel.viewerID) ?? [];
    const studyObj = useViewportActions().getStudyObjectByViewerId(
      sel.viewerID,
    );
    if (!studyObj) {
      throw new Error(
        `Failed to retrieve study object for viewer ${sel.viewerID}`,
      );
    }
    const breathholdState = studyObj.getBreathhold();
    const excludedImages = studyObj.getExcludedImages();
    const start = breathholdState.getStart();
    const end = breathholdState.getEnd();

    const seriesUID = currentStudyStore.getSeriesUID(sel.seriesIndex);
    const selectionKey = `${seriesUID}_${sel.sliceIndex}`;

    if (includeBMORETarget) {
      const bmoreStart = breathholdState.getSalientPhaseLocation('BMORE0');
      const bmoreEnd = breathholdState.getSalientPhaseLocation('BMORE30');
      if (bmoreStart === undefined || bmoreEnd === undefined) {
        alert.error(
          'BMORE start or end location is not defined.',
        );
        isAnalysisActive.value = false;
        throw new Error(
          'BMORE start or end location is not defined',
        );
      }
      const bmoreStartUID = imageUIDsfromStore[bmoreStart];
      const bmoreEndUID = imageUIDsfromStore[bmoreEnd];

      if (
        excludedImages.has(bmoreStartUID)
        || excludedImages.has(bmoreEndUID)
      ) {
        alert.error(
          'BMORE start or end image is in the excluded images for the selected breathhold. Please adjust the breathhold selection.',
        );
        isAnalysisActive.value = false;
        throw new Error(
          'BMORE start or end image is in the excluded images for the selected breathhold',
        );
      }

      if (bmoreStart < start || bmoreEnd > end) {
        alert.error(
          `BMORE range (${bmoreStart}-${bmoreEnd}) is out of bounds for the selected breathhold (${start}-${end}). Please adjust the breathhold selection.`,
        );
        isAnalysisActive.value = false;
        throw new Error(
          'BMORE range is out of bounds for the selected breathhold',
        );
      }

      prefetchObject.bmoreTarget[selectionKey] = [bmoreStart, bmoreEnd];
    }

    prefetchObject.breathHoldCrops[selectionKey] = { start, end };

    const croppedImageUIDs = imageUIDsfromStore.slice(start, end + 1);
    const cleansedImageUIDs = croppedImageUIDs.filter(
      uid => !excludedImages.has(uid),
    );

    prefetchObject.include.push(...cleansedImageUIDs);
    prefetchObject.allImageUIDs.push(...croppedImageUIDs);
    prefetchObject.excludedImageUIDs.push(...excludedImages);
  });

  return prefetchObject;
}

// ─── getSliceClassification ─────────────────────────────────────────
function getSliceClassification() {
  const sliceClassification: Record<string, Orientation> = {};

  confirmedSelections.value.forEach((sel) => {
    sliceClassification[String(sel.dialogSlotIndex)]
      = selectedSliceOrientation.value[String(sel.dialogSlotIndex)]
        ?? seriesClassification.getClassificationByViewport(sel.viewerID);
  });

  return sliceClassification;
}

// ─── buildSliceAnalysisInfo ─────────────────────────────────────────
async function buildSliceAnalysisInfo() {
  // 1. Snapshot confirmed viewport state at dialog-open time
  confirmedSelections.value = viewerIDs.map((viewerID, idx) => {
    const { seriesIdx, sliceIdx }
      = viewportStateStore.getSelectedImageIndices(viewerID);
    return {
      viewerID,
      seriesIndex: seriesIdx,
      sliceIndex: sliceIdx,
      dialogSlotIndex: idx,
    };
  });

  // 2. Build per-slice analysis info driven from confirmedSelections
  const allSliceInfo: SliceAnalysisInfo[] = [];
  isPreflightLoading.value = true;
  preflightLoadError.value = null;
  preflightCurrentSlice.value = null;
  preflightProcessedImages.value = 0;
  preflightTotalImages.value = 0;

  confirmedSelections.value.forEach((sel) => {
    const studyObj = useViewportActions().getStudyObjectByViewerId(
      sel.viewerID,
    );
    if (!studyObj) {
      throw new Error(
        `Failed to retrieve study object for viewer ${sel.viewerID}`,
      );
    }
    const breathholdState = studyObj.getBreathhold();
    const excludedImages = studyObj.getExcludedImages();
    const imageUIDsfromStore
      = currentStudyStore.getSliceImageUIDsbyView(sel.viewerID) ?? [];

    const breathHoldStart = breathholdState.getStart();
    const breathHoldEnd = breathholdState.getEnd();
    const bmoreStart = breathholdState.getSalientPhaseLocation('BMORE0');
    const bmoreEnd = breathholdState.getSalientPhaseLocation('BMORE30');

    const excludedImageIndices: number[] = [];
    let imageCount = 0;

    for (let i = breathHoldStart; i <= breathHoldEnd; i++) {
      const imageUID = imageUIDsfromStore[i];
      if (imageUID) {
        if (excludedImages.has(imageUID)) {
          excludedImageIndices.push(i + 1);
        }
        else {
          imageCount += 1;
        }
      }
    }

    const orientation
      = seriesClassification.getClassificationByViewport(sel.viewerID)
        ?.orientation ?? Orientation.Undefined;
    selectedSliceOrientation.value[String(sel.dialogSlotIndex)] = orientation;

    allSliceInfo.push({
      sliceIndex: sel.dialogSlotIndex,
      viewerID: sel.viewerID,
      seriesIndex: sel.seriesIndex,
      imageCount,
      processedImageCount: 0,
      breathHoldStart,
      breathHoldEnd,
      bmoreStart,
      bmoreEnd,
      segmentedPhaseCount: 0,
      missingPhaseIndices: [],
      excludedImageIndices,
      loadingStatus: 'Pending',
    });
  });

  sliceAnalysisInfo.value = allSliceInfo;
  preflightTotalImages.value = allSliceInfo.reduce(
    (sum, item) => sum + item.imageCount,
    0,
  );

  try {
    for (let idx = 0; idx < sliceAnalysisInfo.value.length; idx++) {
      const sliceInfo = sliceAnalysisInfo.value[idx];
      preflightCurrentSlice.value = sliceInfo.sliceIndex;
      sliceInfo.loadingStatus = 'Loading';

      await yieldToUI();

      const orientation
        = selectedSliceOrientation.value[String(sliceInfo.sliceIndex)]
          ?? seriesClassification.getClassificationByViewport(sliceInfo.viewerID)
            ?.orientation
            ?? Orientation.Undefined;

      await processSliceAnalysis(sliceInfo, orientation);
      sliceInfo.loadingStatus = 'Complete';
    }
  }
  catch (error: any) {
    preflightLoadError.value
      = 'Failed while preparing slice data. Please close and reopen this dialog.';
    console.error('Preflight build failed:', error);
  }
  finally {
    preflightCurrentSlice.value = null;
    isPreflightLoading.value = false;
  }
}

function applySelectedOrientations() {
  sliceAnalysisInfo.value.forEach((sliceInfo) => {
    const orientation
      = selectedSliceOrientation.value[String(sliceInfo.sliceIndex)]
        ?? Orientation.Undefined;

    seriesClassification.adjustClassificationByViewport(sliceInfo.viewerID, {
      orientation,
    });
  });
}

function cancelPreflightDialog() {
  showPreflightDialog.value = false;
}

async function checkCacheForImages(imageUIDs: string[]) {
  try {
    const response = await fetch(`${VITE_OCG_BACKEND_URL}check_cache`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/x-ndjson',
        'API_KEY': '9f24b4b4ea85',
      },
      body: JSON.stringify({ imageUIDs }),
    });
    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: response.statusText }));
      throw new Error(`HTTP error ${response.status}: ${errorData.detail}`);
    }
    const data = await response.json();
    if (data.missing) {
      return data.missing as string[];
    }
    return [] as string[];
  }
  catch (error) {
    console.error('Error fetching images:', error);
    return imageUIDs;
  }
}

async function uploadChunkToCache(
  dicomPullRequestChunk: DicomPullRequest[],
  metaData: any,
) {
  try {
    const response = await fetch(`${VITE_OCG_BACKEND_URL}upload_images`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'API_KEY': '9f24b4b4ea85',
      },
      body: JSON.stringify({
        dicomPullRequestObject: dicomPullRequestChunk,
        metaData,
      }),
    });
    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: response.statusText }));
      throw new Error(`HTTP error ${response.status}: ${errorData.detail}`);
    }
    imageUploadProgress.value += dicomPullRequestChunk.length;
    alert.info(
      `Uploaded ${imageUploadProgress.value} of ${imageUploadTotal.value} images to backend cache... `,
    );
    return true;
  }
  catch (error) {
    console.error('Error uploading images:', error);
    return false;
  }
}

async function uploadImagesToCache(imageUIDs: string[]) {
  let processedDicomRequest: DicomPullRequest[] = [];
  const { dicomPullRequestObject, metaData } = await getAllImageUIDs({
    viewerIDs: uniqueConfirmedSelections.value.map(sel => sel.viewerID),
    include: imageUIDs,
    requiresImageLookup: true,
  });

  // const settings = segmentationSettingsStore.getSettings();

  // if (settings.SEND_IMAGES_DIRECTLY) {
  //   metaData.imagesAreAttached = true;
  //   try {
  //     processedDicomRequest = await getAllAsImages(dicomPullRequestObject);
  //   }
  //   catch /* (error) */ {
  //     alert.error('Failed to fetch images for analysis.');
  //     return false;
  //   }
  // }
  // else {
  processedDicomRequest = dicomPullRequestObject;
  // }

  const chunks = [];
  for (let i = 0; i < processedDicomRequest.length; i += uploadChunkSize) {
    chunks.push(processedDicomRequest.slice(i, i + uploadChunkSize));
  }

  await Promise.all(chunks.map(chunk => uploadChunkToCache(chunk, metaData)));

  return true;
}

async function confirmBreatholdCrop(startUID: string, endUID: string) {
  // First get the corresponding indices for the provided UIDs:
  const indicesStart = currentStudyStore.getIndexByImageUID(startUID);
  const indicesEnd = currentStudyStore.getIndexByImageUID(endUID);

  if (!indicesStart || !indicesEnd) {
    alert.error(
      'Failed to retrieve image indices for breathhold crop confirmation.',
    );
    return;
  }

  // Default buttons (Cancel, Ignore, Continue)
  const userConfirm = await alert.confirm(
    `One or more of the Breath-Hold cycles start with ES instead of ED.
      We recommend cropping as such (${indicesStart.imageIndex + 1}-${
        indicesEnd.imageIndex + 1
      }) for slice ${
        indicesStart.imageSliceIndex + 1
      } and running again. Do you want to automatically crop?`,
  );
  if (userConfirm) {
    const studyObj = useViewportActions().getStudyObjectByImageUID(startUID);
    if (!studyObj) {
      alert.error(
        'Failed to retrieve study object for breathhold crop confirmation.',
      );
      return;
    }
    const breathholdState = studyObj.getBreathhold();
    if (!breathholdState) {
      console.error('Breathhold state not found');
      return;
    }
    breathholdState.updateStartEnd(
      indicesStart.imageIndex,
      indicesEnd.imageIndex,
    );
  }
}

function abortAnalysis() {
  if (!isAnalysisActive.value) return;

  if (analysisAbortController) {
    analysisAbortController.abort();
    analysisAbortController = null;
  }

  isAnalysisActive.value = false;
  alert.warning('Analysis aborted.');
}

async function sendImageData() {
  const devMode = false;
  if (devMode) {
    const testPayload = await fetch(
      '/src/components/analysis/temp_testing.JSON',
    );
    if (!testPayload.ok) throw new Error('Failed to load test JSON');
    const devResponseData = await testPayload.json();
    handleResponse(devResponseData);
    return;
  }
  if (isAnalysisActive.value) {
    abortAnalysis();
    return;
  }

  // Duplicate selection is allowed (warned in dialog) — no hard block.

  isAnalysisActive.value = true;
  alert.info('Preparing data for analysis...This might take a moment.');

  // Collect images that should be included (driven by uniqueConfirmedSelections)
  const {
    include,
    bmoreTarget,
    breathHoldCrops,
    allImageUIDs,
    excludedImageUIDs,
  } = getImageUIDsBmoreUIDs(calculateBMORE.value);

  // Check which images are already cached on the backend
  const missingImageUIDs = await checkCacheForImages(include);
  if (missingImageUIDs.length > 0) {
    imageUploadTotal.value = missingImageUIDs.length;
    imageUploadProgress.value = 0;
    alert.info(
      `Uploading ${imageUploadTotal.value} images to backend cache. Please wait... `,
    );
    const uploadSuccess = await uploadImagesToCache(missingImageUIDs);
    if (!uploadSuccess) {
      alert.error('Failed to upload images to backend cache.');
      isAnalysisActive.value = false;
      return;
    }
    alert.success('Images successfully uploaded to backend cache.');
  }
  else {
    alert.success('All required images are already cached on the backend.');
  }

  alert.success('Starting analysis.');

  const viewerIDsForRequest = uniqueConfirmedSelections.value.map(
    sel => sel.viewerID,
  );

  const { dicomPullRequestObject, imageUIDLookupTable, metaData }
    = await getAllImageUIDs({
      viewerIDs: viewerIDsForRequest,
      include: allImageUIDs,
      exclude: excludedImageUIDs,
      requiresImageLookup: true,
    });

  if (!dicomPullRequestObject) {
    isAnalysisActive.value = false;
    alert.error('No images found for analysis.');
    return;
  }

  alert.info('Preparing segmentation data for analysis...');
  const segmentationData = await getSegmentationData(dicomPullRequestObject);

  if (!segmentationData) {
    isAnalysisActive.value = false;
    return;
  }

  metaData.bmoreTarget = calculateBMORE.value ? bmoreTarget : {};
  metaData.breathHoldCrops = breathHoldCrops;
  metaData.excludedImageUIDs = excludedImageUIDs;
  metaData.sliceClassification = getSliceClassification();
  metaData.calculateBMORE = calculateBMORE.value;
  metaData.calculateBMOREMax = calculateBMOREMax.value;
  metaData.calculateVasoconstrictionMap = calculateVasoconstrictionMap.value;

  const analysisObject: AnalysisObject = {
    DicomPullRequestArray: dicomPullRequestObject,
    ImageUIDLookupTable: imageUIDLookupTable,
    SegmentationData: segmentationData,
    MetaData: metaData,
  };

  // Check whether each UID has contours
  const allUIDs = include;
  const allUIDsWithContours = Object.keys(segmentationData);
  const missingContours = allUIDs.filter(
    uid => !allUIDsWithContours.includes(uid),
  );
  if (missingContours.length > 0) {
    alert.error(
      `Please ensure all images are segmented. Missing contours for ${missingContours.length} images.`,
    );
    isAnalysisActive.value = false;
    return;
  }

  alert.info(
    'Sending data to OCG backend. Please wait for analysis to start...',
  );
  try {
    const analysisMessageLog = ref<string[]>([]);
    isAnalysisActive.value = true;
    analysisAbortController = new AbortController();

    const response = await fetch(`${VITE_OCG_BACKEND_URL}analysis`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/x-ndjson',
        'API_KEY': '9f24b4b4ea85',
      },
      body: JSON.stringify({ analysisObj: analysisObject }),
      signal: analysisAbortController.signal,
    });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: response.statusText }));
      throw new Error(`HTTP error ${response.status}: ${errorData.detail}`);
    }

    if (!response.body) {
      isAnalysisActive.value = false;
      throw new Error('Response body is null');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let analysisComplete = false;

    let responseChunks = {
      masks: [] as any[],
      metadata: null as any,
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.trim() === '') continue;

        try {
          const update = JSON.parse(line);

          if (update.type === 'progress') {
            const duration = update.duration_seconds
              ? `Duration: ${update.duration_seconds} seconds`
              : '';
            alert.info(
              `Analysis Progress: ${update.message} (${update.step}/${update.total_steps}). ${duration}`,
            );
          }
          else if (update.type === 'response_start') {
            responseChunks = { masks: [], metadata: null };
            alert.info('Receiving analysis results...');
          }
          else if (update.type === 'response_chunk') {
            if (update.chunk_type === 'masks') {
              responseChunks.masks.push(update.data);
            }
            else if (update.chunk_type === 'metadata') {
              responseChunks.metadata = update.data;
            }
          }
          else if (update.type === 'response_end') {
            const completeResponse = {
              ...responseChunks.metadata,
              BiomarkerMasks: responseChunks.masks.flat(),
            };

            handleResponse(completeResponse);
            analysisComplete = true;
            analysisMessageLog.value = [];
            // Save state.
            loadSaveAnalysisStateDialog.value?.backgroundAutosave(
              currentStudyStore.getStudyID,
            );
            alert.hideAll();
            isAnalysisActive.value = false;
            appState.currentView = 'GraphicalAnalysis';

            return;
          }
          else if (update.type === 'error') {
            alert.error(`Analysis Error (${update.step}): ${update.message}`);
            isAnalysisActive.value = false;
            return;
          }
          else if (update.type === 'action') {
            const { action } = update.message;
            if (action === 'auto_crop') {
              const { crop_UIDs: cropUIDs } = update.message;
              confirmBreatholdCrop(cropUIDs.start, cropUIDs.end);
              return;
            }
          }
        }
        catch (e) {
          alert.error(`Failed to parse JSON update. Error: ${e}`);
        }
      }
    }

    // Process any remaining data in the buffer
    if (buffer.trim() !== '') {
      try {
        const update = JSON.parse(buffer);
        if (update.type === 'progress') {
          alert.info(
            `Analysis Progress: ${update.message} (${update.step}/${update.total_steps})`,
          );
        }
        else if (update.type === 'result') {
          analysisComplete = true;
        }
        else if (update.type === 'error') {
          alert.error(`Analysis Error (${update.step}): ${update.message}`);
        }
      }
      catch /* (e) */ {
        alert.error('Failed to process final analysis update.');
      }
    }

    if (!analysisComplete) {
      alert.warning(
        'Analysis stream ended without a final result or error message.',
      );
    }
  }
  catch (error: any) {
    if (error.name === 'AbortError') return;
    alert.error(
      `An error occurred during analysis: ${error.message}. Please try again.`,
    );
  }
  finally {
    isAnalysisActive.value = false;
    analysisAbortController = null;
  }
}

async function startAnalysisFromDialog() {
  if (isPreflightLoading.value) {
    alert.info(
      'Still preparing slice data. Please wait for pre-check to finish.',
    );
    return;
  }

  if (preflightLoadError.value) {
    alert.error(preflightLoadError.value);
    return;
  }

  if (hasUnclassifiedOrientations.value) {
    alert.error(
      'Please select an orientation classification for every slice before starting analysis.',
    );
    return;
  }

  applySelectedOrientations(); // For now won't really work since we need per slice classification
  showPreflightDialog.value = false;
  await sendImageData();
}

async function beginAnalysis() {
  if (isAnalysisActive.value) {
    abortAnalysis();
    return;
  }

  showPreflightDialog.value = true;
  buildSliceAnalysisInfo().catch((error) => {
    preflightLoadError.value
      = 'Failed while preparing slice data. Please close and reopen this dialog.';
    isPreflightLoading.value = false;
    console.error('Failed to start preflight build:', error);
  });
}

defineExpose({
  beginAnalysis,
  abortAnalysis,
  isAnalysisActive,
});
</script>

<template>
  <LoadSaveAnalysisStateDialog ref="loadSaveAnalysisStateDialog" />

  <v-dialog v-model="showPreflightDialog" max-width="1120" scrollable>
    <v-card>
      <v-card-title class="text-title-large">
        OCG Analysis Pre-Check
      </v-card-title>
      <v-card-subtitle>
        Review slice-level inputs and choose analysis options before starting.
      </v-card-subtitle>

      <v-card-text>
        <v-alert
          v-if="hasDuplicateSelections"
          type="warning"
          variant="tonal"
          density="comfortable"
          class="mb-4"
        >
          Both viewers are showing the same series and slice. The analysis will
          only process these images once, but results will be displayed in both
          analysis views.
        </v-alert>

        <v-alert
          v-if="hasUnclassifiedOrientations"
          type="warning"
          variant="tonal"
          density="comfortable"
          class="mb-4"
        >
          Select an orientation classification for every slice before starting
          analysis. Undefined orientations cannot be used.
        </v-alert>

        <v-alert
          v-if="isPreflightLoading"
          type="info"
          variant="tonal"
          density="comfortable"
          class="mb-4"
        >
          Preparing images and segmentation coverage for pre-check.
          <div v-if="preflightCurrentSlice !== null" class="text-body-small mt-1">
            Processing Slice {{ preflightCurrentSlice + 1 }}...
          </div>
        </v-alert>

        <v-alert
          v-if="preflightLoadError"
          type="error"
          variant="tonal"
          density="comfortable"
          class="mb-4"
        >
          {{ preflightLoadError }}
        </v-alert>

        <v-progress-linear
          v-if="isPreflightLoading || preflightTotalImages > 0"
          :model-value="preflightProgress"
          color="primary"
          height="10"
          rounded
          class="mb-2"
        />

        <div
          v-if="isPreflightLoading || preflightTotalImages > 0"
          class="preflight-progress-text mb-4"
        >
          {{ preflightProcessedImages }} / {{ preflightTotalImages }} images
          processed
        </div>

        <div v-if="hasSliceSummaries" class="slice-grid">
          <v-card
            v-for="sliceInfo in sliceAnalysisInfo"
            :key="sliceInfo.viewerID"
            variant="outlined"
            class="slice-card"
          >
            <v-card-title class="text-body-large">
              Slice {{ sliceInfo.sliceIndex + 1 }} - {{ sliceInfo.viewerID }}
            </v-card-title>
            <v-card-text>
              <v-select
                v-model="selectedSliceOrientation[String(sliceInfo.sliceIndex)]"
                :items="orientationOptions"
                item-title="label"
                item-value="value"
                label="Slice Orientation Classification"
                density="compact"
                variant="outlined"
                :disabled="isPreflightLoading"
                hide-details
                class="mb-3"
                @update:model-value="
                  handleSliceOrientationChange(sliceInfo, $event)
                "
              />

              <div class="slice-row">
                <span class="slice-label">Num Images:</span>
                <span>{{ sliceInfo.imageCount }}</span>
              </div>
              <div class="slice-row">
                <span class="slice-label">Prepared Images:</span>
                <span>{{ sliceInfo.processedImageCount }} /
                  {{ sliceInfo.imageCount }}</span>
              </div>
              <div class="slice-row">
                <span class="slice-label">Breathhold Range:</span>
                <span>{{ sliceInfo.breathHoldStart + 1 }} -
                  {{ sliceInfo.breathHoldEnd + 1 }}</span>
              </div>
              <div v-if="calculateBMORE" class="slice-row">
                <span class="slice-label">BMORE Phases:</span>
                <span>{{ sliceInfo.bmoreStart !== undefined
                  ? sliceInfo.bmoreStart + 1
                  : 'N/A' }} -
                  {{ sliceInfo.bmoreEnd !== undefined
                    ? sliceInfo.bmoreEnd + 1
                    : 'N/A' }}</span>
              </div>
              <div class="slice-row">
                <span class="slice-label">Num Phases with Segmentation:</span>
                <span>{{ sliceInfo.segmentedPhaseCount }}</span>
              </div>
              <div class="slice-row">
                <span class="slice-label">Missing segmentation phases (index):</span>
                <span>
                  {{
                    sliceInfo.missingPhaseIndices.length > 0
                      ? sliceInfo.missingPhaseIndices.join(', ')
                      : 'None'
                  }}
                  <span
                    v-if="sliceInfo.missingPhaseIndices.length > 0"
                    class="missing-warning"
                  >
                    Warning: required segmentation phases are missing.
                  </span>
                </span>
              </div>
              <div class="slice-row">
                <span class="slice-label">Excluded images (index):</span>
                <span>
                  {{
                    sliceInfo.excludedImageIndices.length > 0
                      ? sliceInfo.excludedImageIndices.join(', ')
                      : 'None'
                  }}
                </span>
              </div>
            </v-card-text>
          </v-card>
        </div>

        <v-divider class="my-4" />

        <div class="text-body-large mb-2">
          Global Analysis Options
        </div>
        <v-checkbox
          v-model="calculateBMORE"
          label="Calculate BMORE"
          density="compact"
          hide-details
        />
        <v-checkbox
          v-model="calculateBMOREMax"
          label="Calculate BMORE Max"
          density="compact"
          hide-details
        />
        <v-checkbox
          v-model="calculateVasoconstrictionMap"
          label="Calculate Vasoconstriction Map"
          density="compact"
          hide-details
        />
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="cancelPreflightDialog">
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          :disabled="
            isPreflightLoading
              || !!preflightLoadError
              || hasMissingSegmentationPhases
              || hasUnclassifiedOrientations
          "
          @click="startAnalysisFromDialog"
        >
          Start Analysis
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.slice-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  gap: 12px;
}

.slice-card {
  border-radius: 10px;
}

.slice-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
}

.slice-label {
  font-weight: 600;
}

.preflight-progress-text {
  font-size: 0.9rem;
}

.missing-warning {
  color: rgb(var(--v-theme-warning));
  font-weight: 600;
  margin-left: 8px;
}
</style>
